import React from "react";
import { Link } from "react-router-dom";
import Logo from "../../components/Logo";
import Footer from "../../components/Footer";
import { ShieldCheck, Bot, BarChart3, Target, Lock, Zap, Globe, Mail, ArrowLeft } from "lucide-react";

export default function About() {
  return (
    <div className="au-root">
      <header style={S.header}>
        <Link to="/" style={{ textDecoration: "none" }}><Logo size="medium" /></Link>
        <Link to="/" className="au-pill au-pill-glass au-pill-sm">
          <ArrowLeft /> Home
        </Link>
      </header>

      <div className="au-container" style={S.container}>
        <div className="au-eyebrow" style={{ marginBottom: 14 }}>About Us</div>
        <h1 style={S.h1}>
          Building <span style={{ color: "var(--au-accent-ink)" }}>Cybersecurity</span> for Everyone
        </h1>
        <p className="au-lead" style={{ marginBottom: 48 }}>
          Aegis is a product by <span className="au-em">getkhojo.com</span> — making professional security tools accessible to small businesses, developers, and security teams worldwide.
        </p>

        <section style={S.section}>
          <h2 style={S.h2}>Our Mission</h2>
          <p style={S.p}>
            Most cybersecurity tools cost ₹50,000+ per year and require expert teams to operate. We believe security should be a right, not a luxury. Aegis brings enterprise-grade security scanning to anyone with a domain — at prices that work for everyone.
          </p>
        </section>

        <section style={S.section}>
          <h2 style={S.h2}>What We Do</h2>
          <div style={S.grid}>
            <div className="au-glass au-pad au-lift">
              <div className="au-badge" style={{ marginBottom: 14 }}><ShieldCheck /></div>
              <h3 style={S.h3}>Security Scanning</h3>
              <p style={S.cardP}>AI-powered vulnerability detection for websites, APIs, and infrastructure. Find issues before attackers do.</p>
            </div>
            <div className="au-glass au-pad au-lift">
              <div className="au-badge" style={{ marginBottom: 14 }}><Bot /></div>
              <h3 style={S.h3}>AI Insights</h3>
              <p style={S.cardP}>Plain-English explanations of complex security findings. Built for developers and non-experts alike.</p>
            </div>
            <div className="au-glass au-pad au-lift">
              <div className="au-badge" style={{ marginBottom: 14 }}><BarChart3 /></div>
              <h3 style={S.h3}>Detailed Reports</h3>
              <p style={S.cardP}>Comprehensive PDF, CSV, and DOCX reports with remediation steps prioritized by risk.</p>
            </div>
          </div>
        </section>

        <section style={S.section}>
          <h2 style={S.h2}>Our Values</h2>
          <div style={{ marginTop: 16 }}>
            <Value Icon={Target} title="Honesty First" desc="No 100% accuracy claims. We tell you exactly what Aegis can and cannot do." />
            <Value Icon={Lock} title="Privacy Respected" desc="Your scan data stays yours. We don't sell, share, or analyze it." />
            <Value Icon={Zap} title="Speed Matters" desc="Most scans complete in under 2 minutes. No bureaucracy." />
            <Value Icon={Globe} title="Accessible Pricing" desc="Free tier available. Premium plans start at ₹4,099/month." />
          </div>
        </section>

        <section style={S.section}>
          <h2 style={S.h2}>The Team</h2>
          <p style={S.p}>
            Aegis is built by a small, focused team at <span className="au-em">getkhojo.com</span>. We're developers, security researchers, and AI engineers who got tired of overpriced security tools that nobody could understand.
          </p>
          <p style={S.p}>
            Currently bootstrapped, building in public. We're growing fast and looking for users, feedback, and (soon) team members.
          </p>
        </section>

        <section className="au-glass au-pad-lg au-center" style={{ marginTop: 16, marginBottom: 48 }}>
          <h2 style={{ ...S.h2, marginTop: 0 }}>Get in Touch</h2>
          <p style={{ ...S.p, marginBottom: 18 }}>Questions, feedback, or partnership opportunities?</p>
          <a href="mailto:support@getkhojo.com" className="au-pill au-pill-primary">
            <Mail /> support@getkhojo.com
          </a>
        </section>

      </div>
      <Footer />
    </div>
  );
}

function Value({ Icon, title, desc }) {
  return (
    <div style={{ display: "flex", gap: 16, padding: "18px 0", borderBottom: "1px solid var(--au-hair)" }}>
      <div className="au-badge" style={{ width: 38, height: 38, borderRadius: 12 }}>
        <Icon style={{ width: 20, height: 20 }} />
      </div>
      <div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--au-text)", marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, color: "var(--au-muted)", lineHeight: 1.7 }}>{desc}</div>
      </div>
    </div>
  );
}

const S = {
  header: { maxWidth: 900, margin: "0 auto", padding: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 },
  container: { maxWidth: 900, padding: "40px 24px 60px" },
  h1: { fontSize: "clamp(2rem, 5vw, 3rem)", margin: "0 0 24px" },
  h2: { fontSize: "clamp(1.4rem, 3vw, 1.8rem)", color: "var(--au-text)", margin: "0 0 16px" },
  h3: { fontSize: 18, fontWeight: 700, color: "var(--au-text)", margin: "0 0 10px" },
  p: { fontSize: 15, color: "var(--au-text-2)", lineHeight: 1.8, marginBottom: 14 },
  cardP: { fontSize: 13, color: "var(--au-muted)", lineHeight: 1.7, margin: 0 },
  section: { marginBottom: 48 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 20, marginTop: 20 },
  footer: { textAlign: "center", padding: "40px 0 20px", color: "var(--au-muted)", fontSize: 12, borderTop: "1px solid var(--au-hair)", marginTop: 40 }
};
